import { ChevronLeft, ChevronRight, Search } from './icons.tsx'
import {
  useEffect,
  useState,
  type ComponentProps,
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react'
import { cx } from './cx.ts'

/**
 * `stack`: on phones each row is a small card (see `.table-stack` in styles.css);
 * `"cards"` draws each as a rounded card of its own, for lists built with `RowTitle`.
 */
export function Table({
  className,
  stack,
  ...rest
}: HTMLAttributes<HTMLTableElement> & { stack?: boolean | 'cards' }) {
  return (
    <div className="overflow-x-auto">
      <table
        className={cx(
          'w-full border-collapse text-sm',
          stack && 'table-stack',
          stack === 'cards' && 'table-cards',
          className,
        )}
        {...rest}
      />
    </div>
  )
}

/** The row above a list: its switch and pills on the left, the search on the right. */
export function ListToolbar({ children }: { children: ReactNode }) {
  return <div className="mb-3 flex flex-wrap items-center gap-2.5">{children}</div>
}

/** Around a `Table stack="cards"`: one card from 640px, nothing on a phone (the rows are the cards). */
export function ListFrame({ className, ...rest }: ComponentProps<'div'>) {
  return (
    <div
      className={cx('sm:overflow-hidden sm:rounded-card sm:bg-surface sm:shadow-card', className)}
      {...rest}
    />
  )
}

/**
 * What goes in the first cell of a list row: a tile (an `IconTile`, an `Avatar` or a
 * photo), the row's name (pass the `Link` that opens it) and a quieter second line.
 */
export function RowTitle({
  tile,
  title,
  sub,
}: {
  tile?: ReactNode
  title: ReactNode
  sub?: ReactNode
}) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      {tile}
      <span className="min-w-0">
        <span className="block truncate font-semibold text-ink">{title}</span>
        {sub && <span className="block truncate text-[12.5px] font-normal text-muted">{sub}</span>}
      </span>
    </span>
  )
}

const NOTE_TONE = {
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
} as const

/**
 * Where a row stands, in two short lines ("26 of 64 arrived", "Due 6 days ago"). On a
 * phone card they run on one line. `tone` colours the line that needs the eye.
 */
export function RowNote({
  main,
  sub,
  tone,
  toneOn = 'main',
}: {
  main: ReactNode
  sub?: ReactNode
  tone?: keyof typeof NOTE_TONE
  toneOn?: 'main' | 'sub'
}) {
  const colour = tone && NOTE_TONE[tone]
  return (
    <span className="block min-w-0 text-[13px] max-sm:text-xs">
      <span
        className={cx(
          'font-semibold sm:block sm:truncate',
          toneOn === 'main' && colour ? colour : 'text-ink max-sm:font-medium max-sm:text-muted',
        )}
      >
        {main}
      </span>
      {sub && (
        <>
          <span className="text-subtle sm:hidden"> · </span>
          <span
            className={cx(
              'sm:block sm:truncate sm:text-[12.5px]',
              toneOn === 'sub' && colour ? cx(colour, 'font-semibold') : 'text-muted',
            )}
          >
            {sub}
          </span>
        </>
      )}
    </span>
  )
}

/**
 * The buttons at the end of a list row: in a line on the right, across the bottom of a
 * phone card. A click on one never opens the row. Renders nothing when it has none.
 */
export function RowActions({ children }: { children?: ReactNode }) {
  if (!children || (Array.isArray(children) && !children.some(Boolean))) return null
  return (
    <div
      className="flex items-center justify-end gap-1.5 max-sm:gap-2 max-sm:[&>*]:h-10 max-sm:[&>*]:flex-1"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}

const AVATAR = {
  people: 'bg-people-light text-people',
  buy: 'bg-buy-light text-buy',
} as const

/** A person or business as initials on a soft tile of its area's colour, for rows with no picture. */
export function Avatar({ name, area = 'people' }: { name: string; area?: keyof typeof AVATAR }) {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const initials = (
    (words[0]?.[0] ?? '') + (words.length > 1 ? words.at(-1)![0] : '')
  ).toUpperCase()
  return (
    <span
      aria-hidden
      className={cx(
        'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
        AVATAR[area],
      )}
    >
      {initials || '?'}
    </span>
  )
}

export function TH({
  className,
  align,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: 'right' }) {
  return (
    <th
      className={cx(
        'border-b border-border px-3 py-2.5 text-left text-table-head text-muted',
        align === 'right' && 'text-right',
        className,
      )}
      {...rest}
    />
  )
}

export function TD({
  className,
  align,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: 'right' }) {
  return (
    <td
      className={cx(
        'border-b border-border px-3 py-2.5',
        align === 'right' && 'tabular text-right',
        className,
      )}
      {...rest}
    />
  )
}

export function TR({ className, onClick, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cx(onClick && 'cursor-pointer hover:bg-soft', className)}
      onClick={onClick}
      {...rest}
    />
  )
}

/** Search box that reports its value after the user pauses typing. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  delay = 250,
  className,
  autoFocus,
  end,
  listKeys,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  delay?: number
  className?: string
  autoFocus?: boolean
  /** A small button inside the box, at its end (the camera scanner on Products). */
  end?: ReactNode
  /** The box of a list with `useListKeys`: while it has focus a hint says what the keys do. */
  listKeys?: boolean
}) {
  const [text, setText] = useState(value)
  // Follow outside resets (e.g. a "clear" button) without an effect.
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    setText(value)
  }
  useEffect(() => {
    if (text === value) return
    const timer = setTimeout(() => onChange(text), delay)
    return () => clearTimeout(timer)
  }, [text, value, delay, onChange])
  return (
    <div className={cx('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle"
        aria-hidden
      />
      <input
        type="search"
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => setText(e.target.value)}
        // The list's keys work from this box (`useListKeys`): they send the text at once,
        // and `data-pending` tells them the rows on screen are not its results yet.
        onKeyDown={(e) => {
          if (text !== value && /^(ArrowUp|ArrowDown|Enter)$/.test(e.key)) onChange(text)
        }}
        data-pending={text !== value ? '' : undefined}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cx(
          'peer w-full rounded-xl border border-border bg-surface py-2 pl-9 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30',
          end ? 'pr-11' : 'pr-3',
        )}
      />
      {end && <div className="absolute right-1 top-1/2 flex -translate-y-1/2">{end}</div>}
      {listKeys && (
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 hidden items-center gap-1.5 whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs text-surface shadow-card lg:peer-focus:flex"
        >
          <HintKey>↑</HintKey> <HintKey>↓</HintKey> pick a row <HintKey>Enter</HintKey> open{' '}
          <HintKey>←</HintKey> <HintKey>→</HintKey> stay in the box
        </div>
      )}
    </div>
  )
}

function HintKey({ children }: { children: ReactNode }) {
  return (
    <kbd className="min-w-[18px] rounded-lg border border-surface/25 border-b-2 px-1 text-center font-sans text-[11px] font-bold">
      {children}
    </kbd>
  )
}

export function Pagination({
  offset,
  limit,
  total,
  onChange,
}: {
  offset: number
  limit: number
  total: number
  onChange: (offset: number) => void
}) {
  if (total <= limit) return null
  const page = Math.floor(offset / limit) + 1
  const pages = Math.ceil(total / limit)
  return (
    <div className="flex items-center justify-between px-3 py-3 text-sm text-muted">
      <span>
        {offset + 1}–{Math.min(offset + limit, total)} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onChange(Math.max(0, offset - limit))}
          className="rounded-xl p-1.5 hover:bg-soft disabled:opacity-40"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="tabular px-2">
          {page} / {pages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={page >= pages}
          onClick={() => onChange(offset + limit)}
          className="rounded-xl p-1.5 hover:bg-soft disabled:opacity-40"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  )
}
